import React from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
    QueryClient,
    QueryClientProvider as ReactQueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // disable automatic refetching on window focus
            retry: 0, // already handled by axiosRetry on axios instance
        },
    },
});

const ReactQueryDevtoolsProduction = React.lazy(() =>
    // eslint-disable-next-line import/extensions
    import('@tanstack/react-query-devtools/build/lib/index.prod.js').then((d) => ({
        default: d.ReactQueryDevtools,
    }))
);

export const QueryClientProvider = ({ children }) => {
    const [showDevtools, setShowDevtools] = React.useState(false);
    const { authState } = useOktaAuth();

    React.useEffect(() => {
        window.toggleReactQueryDevtools = () => setShowDevtools((old) => !old);
    }, []);

    React.useEffect(() => {
        if (authState === null) return; // do nothing if authState is loading (null)
        if (!authState?.isAuthenticated) {
            try {
                // Clear all queries and cache on logout
                queryClient.clear();
                console.log('Query cache cleared on logout');
            } catch (error) {
                console.error('Error clearing query cache:', error);
            }
        }
    }, [authState]);

    return (
        <ReactQueryClientProvider client={queryClient}>
            {children}
            <ReactQueryDevtools
                initialIsOpen={false}
                toggleButtonProps={{ style: { display: 'none' } }}
            />
            {showDevtools && (
                <React.Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction />
                </React.Suspense>
            )}
        </ReactQueryClientProvider>
    );
};

export default QueryClientProvider;
